import { Location, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';

import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterLink, RouterOutlet } from '@angular/router';
import { BackService } from '@bp-core/src/lib/services/back.service';
import { fuseAnimations } from '@fuse/animations';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseConfig, FuseConfigService } from '@fuse/services/config';
import { BpHeaderCenterConfig, BpHeaderConfig, TailwindBreakpoint } from 'bp/shared/models/header-config';
import { BpHeaderService } from 'bp/shared/services/bp-header.service';
import { BpHistoryService } from 'bp/shared/services/bp-history.service';
import { BpScrollHeaderService } from 'bp/shared/services/bp-scroll-header.service';
import { RedirectService } from 'projects/bp-core/src/lib/services/portal/redirect.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'bp-steps-layout',
    templateUrl: './bp-steps.component.html',
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
    imports: [
        MatIconModule,
        RouterOutlet,
        FuseLoadingBarComponent,
        MatButtonModule,
        RouterLink,
        NgClass,
        NgTemplateOutlet,
    ]
})
export class BpStepsLayoutComponent implements OnInit, OnDestroy, AfterViewInit {
  isFirstStep: boolean | undefined;

  /**
   * Ancla invisible al tope del contenido. Mientras se ve, la vista está sin scrollear;
   * cuando sale del viewport, el header pasa a su estado translúcido. Mismo mecanismo que
   * el layout `bp` (ver su `ngAfterViewInit`).
   */
  @ViewChild('anchorElement') anchorElement!: ElementRef<HTMLElement>;

  /**
   * true cuando el contenido ya se scrolleó bajo el header: activa el fondo translúcido
   * con blur y la sombra, en lugar del blanco opaco con borde inferior.
   *
   * Se arma con dos señales porque hay dos formas de scrollear en este layout:
   * el ancla propia (contenido Angular normal, ej. Descubrir) y lo que informe la vista
   * cuando su contenido scrollea fuera del documento (ej. el iframe del blog).
   */
  get shadow(): boolean {
    return this.anchorOutOfView || this.contentScrolled;
  }

  /** El ancla al tope del documento salió de la vista (scroll del propio documento). */
  private anchorOutOfView = false;

  /** La vista informó su scroll vía `BpHeaderService.setContentScrolled` (iframe). */
  private contentScrolled = false;

  private scrollAnchorObserver?: IntersectionObserver;

  appConfig: FuseConfig;
  headerConfig: BpHeaderConfig | null = null;
  centerConfig: BpHeaderCenterConfig | null = null;
  /**
   * Contenido que la directiva `bpScrollHeader` publica cuando el bloque observado
   * sale de la vista (mismo contrato que el layout `bp`), para que las vistas con
   * este layout también tengan header scroll-aware en mobile.
   */
  scrollHeaderContent: TemplateRef<any> | null = null;
  /** Header pegado al top mientras se scrollea (opt-in por vista, ver BpHeaderService) */
  stickyHeader = false;
  unsubscribe$: Subject<boolean> = new Subject<boolean>();

  // Tailwind visibility class mappings
  private readonly hideFromClasses: Record<TailwindBreakpoint, string> = {
    sm: 'sm:hidden',
    md: 'md:hidden',
    lg: 'lg:hidden',
    xl: 'xl:hidden',
    '2xl': '2xl:hidden',
  };

  private readonly showFromClasses: Record<TailwindBreakpoint, string> = {
    sm: 'hidden sm:block',
    md: 'hidden md:block',
    lg: 'hidden lg:block',
    xl: 'hidden xl:block',
    '2xl': 'hidden 2xl:block',
  };
  constructor(
    public backService: BackService,
    private location: Location,
    private _fuseConfigService: FuseConfigService,
    private router: Router,
    private route: ActivatedRoute,
    private redirectService: RedirectService,
    private bpHeaderService: BpHeaderService,
    private bpScrollHeaderService: BpScrollHeaderService,
    private historyService: BpHistoryService,
  ) {}

  ngAfterViewInit(): void {
    this.scrollAnchorObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          this.anchorOutOfView = !entry.isIntersecting;
        });
      },
      { threshold: 0.1 },
    );

    this.scrollAnchorObserver.observe(this.anchorElement.nativeElement);
  }

  ngOnDestroy(): void {
    this.scrollAnchorObserver?.disconnect();
    this.unsubscribe$.next(true);
    this.unsubscribe$.unsubscribe();
  }

  ngOnInit() {
    this._fuseConfigService.config$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((config: FuseConfig) => {
        // Store the config
        this.appConfig = config;
      });
    this.backService.initializeService();
    this.backService.isFirstStepObs.pipe(takeUntil(this.unsubscribe$)).subscribe(isFirstStep => {
      setTimeout(() => {
        this.isFirstStep = isFirstStep;
      }, 0);
    });

    // Subscribe to header configuration for center zone customization (template-based)
    // setTimeout difiere la asignación fuera del ciclo de CD en curso: un componente
    // hijo del router-outlet puede mutar el header durante la detección de cambios
    // (p. ej. un effect que reacciona a una transición de estado), y asignarlo de forma
    // síncrona dispararía NG0100 (ExpressionChangedAfterItHasBeenChecked) acá. Mismo
    // guard que ya se usa para isFirstStep arriba.
    this.bpHeaderService.headerConfig$.pipe(takeUntil(this.unsubscribe$)).subscribe(config => {
      setTimeout(() => {
        this.headerConfig = config;
      }, 0);
    });

    // Subscribe to simple text-based center configuration
    this.bpHeaderService.centerConfig$.pipe(takeUntil(this.unsubscribe$)).subscribe(config => {
      setTimeout(() => {
        this.centerConfig = config;
      }, 0);
    });

    // Subscribe to sticky header opt-in
    this.bpHeaderService.stickyHeader$.pipe(takeUntil(this.unsubscribe$)).subscribe(sticky => {
      setTimeout(() => {
        this.stickyHeader = sticky;
      }, 0);
    });

    // Vistas cuyo contenido scrollea fuera del documento (el iframe del blog): el ancla de
    // arriba nunca se mueve, así que el estado del header lo informa la propia vista.
    // Mismo setTimeout que el resto para no disparar NG0100.
    this.bpHeaderService.contentScrolled$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(scrolled => {
        setTimeout(() => {
          this.contentScrolled = scrolled;
        }, 0);
      });

    // Subscribe to scroll-based header content (directiva bpScrollHeader).
    // Mismo setTimeout que arriba para no disparar NG0100 cuando la directiva
    // publica el template durante la detección de cambios del hijo.
    this.bpScrollHeaderService.scrollContent$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(template => {
        setTimeout(() => {
          this.scrollHeaderContent = template;
        }, 0);
      });
  }

  /**
   * Get CSS classes for center config visibility based on hideFrom/showFrom settings
   */
  getCenterConfigClasses(): string {
    if (!this.centerConfig) return '';

    const classes: string[] = [];

    if (this.centerConfig.hideFrom) {
      classes.push(this.hideFromClasses[this.centerConfig.hideFrom]);
    }

    if (this.centerConfig.showFrom) {
      classes.push(this.showFromClasses[this.centerConfig.showFrom]);
    }

    if (this.centerConfig.customClass) {
      classes.push(this.centerConfig.customClass);
    }

    return classes.join(' ');
  }
  back() {
    this.backService.back();
  }
  close() {
    //solucion temporal
    /*let urlArray = this.router.url.split('/').filter(segment => segment !== '');
    if (urlArray.length > 1 && (urlArray[0] == 'deposit' || urlArray[0] == 'rescue')) {
      this.redirectService.redirect(environment.urlPortal + '/goals/' + urlArray[1]);
      return;
    }
    this.redirectService.redirect(environment.urlPortal);*/

    let urlArray = this.router.url.split('/').filter(segment => segment !== '');
    urlArray = urlArray.map(segment => segment.split('?')[0]);
    if (urlArray.length == 2 && urlArray[0] === 'entity' && urlArray[1] === 'new') {
      this.router.navigate(['/dashboard']);
      return;
    }
    if (urlArray.length == 3 && urlArray[0] === 'entity' && urlArray[1] === 'onboarding') {
      this.router.navigate(['/entity/activate']);
      return;
    }
    if (
      urlArray.length > 1 &&
      urlArray[0] == 'goals' &&
      (urlArray[1] == 'new' || urlArray[1] == 'start')
    ) {
      this.historyService.closeFlow(this.router.createUrlTree(['/dashboard']));
      return;
    }
    // Las salidas hacia la meta o el inicio van por `closeFlow`: saca el flujo del camino de
    // "atrás" (el de la app y el de Android/iOS), en vez de apilar la meta encima de él.
    if (
      urlArray.length > 1 &&
      (urlArray[0] == 'deposit' || urlArray[0] == 'rescue' || urlArray[0] == 'goals')
    ) {
      if (urlArray[1] === 'apv-flow') {
        this.historyService.closeFlow(
          this.router.createUrlTree(['/goals', urlArray[2]], { fragment: 'summary' }),
        );
        return;
      }
      const queryParams = new URLSearchParams(this.router.url.split('?')[1]);
      const sourceParam = queryParams.get('source');
      const returnUrl = queryParams.get('returnUrl');
      // Si tiene returnUrl, el usuario navegó desde otro lugar; volver atrás en el historial
      if (returnUrl) {
        this.location.back();
        return;
      }
      // Si viene del dashboard, volver al dashboard
      if (sourceParam === 'dashboard') {
        this.historyService.closeFlow(this.router.createUrlTree(['/dashboard']));
        return;
      }
      // Cuando viene de buying-power (stocks), volver atrás en el historial.
      // El user llega desde /stocks/:id y location.back() lo deja exactamente ahí,
      // sin sumar una entry extra al historial.
      if (sourceParam === 'buying-power') {
        this.location.back();
        return;
      }
      // Default: la meta. Si el flujo se abrió desde ella (depositar, editar desde ajustes),
      // vuelve a la vista en que estaba; si no (p. ej. el depósito que sigue a crear la
      // meta), muestra el resumen en lugar del flujo. Un location.back() a secas no sirve:
      // tras crear la meta mandaría al flujo de creación.
      this.historyService.closeFlow(
        this.router.createUrlTree(['/goals', urlArray[1]], { fragment: 'summary' }),
      );
      return;
    }

    if (
      urlArray.length > 0 &&
      [
        'onboarding',
        'schedule-advisor',
        'validateci',
        'contract',
        'discover',
        'confirmation',
      ].includes(urlArray[0])
    ) {
      this.router.navigate(['/dashboard']);
      return;
    }

    // Si la vista tiene contenido embebido que navegó por su cuenta (un iframe), esas
    // entradas están arriba de la de la pantalla en el historial: se saltan todas de
    // una para cerrar de verdad, en vez de ir deshaciendo la navegación de adentro.
    // Vale 0 en el resto de las vistas, donde esto equivale al back de siempre.
    const embeddedDepth = this.backService.embeddedHistoryDepth;
    if (embeddedDepth > 0) {
      this.location.historyGo(-(embeddedDepth + 1));
      return;
    }

    this.location.back();
  }
}
